import { i18n } from "@tellescope/i18n";

/** Extract year, month (1-indexed), and day for a date in the given IANA timezone. */
function getDatePartsInTimezone(date: Date, timeZone?: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  return {
    year: Number(parts.find((p) => p.type === "year")!.value),
    month: Number(parts.find((p) => p.type === "month")!.value),
    day: Number(parts.find((p) => p.type === "day")!.value),
  };
}

export type DateFormat =
  | "MMM D"
  | "MMM D, YYYY"
  | "MMMM D, YYYY"
  | "MM/DD/YYYY"
  | "MM/DD/YY"
  | "MM-DD-YYYY"
  | "DD-MM-YYYY"
  | "MM-DD-YY"
  | "DD-MM-YY";
export type TimeFormat = "hh:mm a" | "hh:mm:ss a" | "HH:mm" | "HH:mm:ss";

// Template literal type that generates all valid date-time format combinations
export type DateTimeFormat = `${DateFormat} at ${TimeFormat}`;

export type RelativeOptions = {
  unit: "days" | "weeks" | "months" | "years";
  amount: number;
  direction: "future" | "past" | "both";
  approximate?: boolean | "weeks" | "months" | "years";
};

// Extract date format from a datetime format string
type ExtractDateFormat<T extends DateTimeFormat> = T extends `${infer D extends DateFormat} at ${string}` ? D : never;

// Extract time format from a datetime format string
type ExtractTimeFormat<T extends DateTimeFormat> = T extends `${string} at ${infer Time extends TimeFormat}`
  ? Time
  : never;

/**
 * Formats a date using the specified format string with optional relative formatting.
 *
 * @param date - The date to format (Date object, string, or timestamp number)
 * @param format - The date format to use. Defaults to "MMM D, YYYY" (e.g., "Jan 15, 2024")
 *   - "MMM D": Short month format without year (e.g., "Jan 15")
 *   - "MMM D, YYYY": Short month format (e.g., "Jan 15, 2024")
 *   - "MMMM D, YYYY": Full month format (e.g., "January 15, 2024")
 *   - "MM/DD/YYYY": Numeric format with full year (e.g., "01/15/2024")
 *   - "MM/DD/YY": Numeric format with 2-digit year (e.g., "01/15/24")
 *   - "MM-DD-YYYY": Numeric format with dashes and full year (e.g., "01-15-2024")
 *   - "MM-DD-YY": Numeric format with dashes and 2-digit year (e.g., "01-15-24")
 *   - "DD-MM-YYYY": Day-first format with dashes and full year (e.g., "15-01-2024")
 *   - "DD-MM-YY": Day-first format with dashes and 2-digit year (e.g., "15-01-24")
 * @param relative - Controls relative date formatting:
 *   - true: Returns "Today", "Tomorrow", "Yesterday" when applicable
 *   - "always": Always returns relative format (e.g., "2 days ago", "about 3 weeks from now")
 *   - RelativeOptions: Fine-grained control over when to stop using relative dates
 *     - unit: The time unit to measure (days, weeks, months, years)
 *     - amount: The maximum delta before switching to absolute format
 *     - direction: Whether to apply the limit to "future", "past", or "both"
 *     - approximate: Controls when "about" is applied (default: false)
 *       - true: Apply "about" to all time units
 *       - false: Never apply "about"
 *       - "weeks": Apply "about" starting from weeks (not days)
 *       - "months": Apply "about" starting from months (not days/weeks)
 *       - "years": Apply "about" only to years (not days/weeks/months)
 * @param timezone - Optional IANA timezone string (e.g., "America/New_York"). Defaults to the user's local timezone.
 * @returns The formatted date string or relative term
 *
 * @example
 * formatDate(new Date(), "MMM D, YYYY") // "Jan 15, 2024"
 * formatDate(new Date(), "MMMM D, YYYY") // "January 15, 2024"
 * formatDate(new Date(), "MM/DD/YYYY") // "01/15/2024"
 * formatDate(new Date(), "MM/DD/YY") // "01/15/24"
 * formatDate(new Date(), "MM-DD-YYYY") // "01-15-2024"
 * formatDate(new Date(), "DD-MM-YYYY") // "15-01-2024"
 * formatDate(new Date(), "MMM D, YYYY", true) // "Today"
 * formatDate(dateInPast, "MMM D, YYYY", { unit: "days", amount: 7, direction: "both" }) // "3 days ago" (within 7 days) or "Jan 10, 2024" (beyond 7 days)
 * formatDate(dateInPast, "MMM D, YYYY", { unit: "weeks", amount: 2, direction: "both", approximate: "weeks" }) // "3 days ago" (no "about") or "about 1 week ago" ("about" for weeks+)
 * formatDate(new Date(), "MMM D, YYYY", false, "America/New_York") // "Jan 15, 2024" (in Eastern time)
 */
export function formatDate(
  date: Date | string | number,
  format: DateFormat = "MMM D, YYYY",
  relative?: boolean | "always" | RelativeOptions,
  timezone?: string
): string {
  try {
    const dateObj = new Date(date);

    if (relative) {
      const now = new Date();
      const nowParts = getDatePartsInTimezone(now, timezone);
      const targetParts = getDatePartsInTimezone(dateObj, timezone);
      const today = new Date(nowParts.year, nowParts.month - 1, nowParts.day);
      const targetDate = new Date(targetParts.year, targetParts.month - 1, targetParts.day);

      const diffInMs = targetDate.getTime() - today.getTime();
      const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));

      if (diffInDays === 0) return i18n.t("Today", { ns: "dates" });
      if (diffInDays === 1) return i18n.t("Tomorrow", { ns: "dates" });
      if (diffInDays === -1) return i18n.t("Yesterday", { ns: "dates" });

      // Convert "always" to RelativeOptions shorthand
      // Note: approximate is handled differently - only for weeks/months/years, not days
      const relativeOptions: RelativeOptions | null =
        relative === "always"
          ? { unit: "years", amount: Infinity, direction: "both", approximate: false }
          : typeof relative === "object"
          ? relative
          : null;

      // Handle RelativeOptions
      if (relativeOptions) {
        const { unit, amount, direction, approximate = false } = relativeOptions;
        const absDiff = Math.abs(diffInDays);
        const isFuture = diffInDays > 0;

        // Convert the amount to days based on unit
        const maxDays = (() => {
          if (amount === Infinity) return Infinity;
          switch (unit) {
            case "days":
              return amount;
            case "weeks":
              return amount * 7;
            case "months":
              return amount * 30; // Approximate
            case "years":
              return amount * 365; // Approximate
          }
        })();

        // Check if we should use relative formatting based on direction
        const shouldUseRelative = (() => {
          if (direction === "both") return absDiff <= maxDays;
          if (direction === "future") return isFuture && diffInDays <= maxDays;
          if (direction === "past") return !isFuture && absDiff <= maxDays;
          return false;
        })();

        // If outside the threshold, fall through to absolute formatting
        if (!shouldUseRelative) {
          // Fall through to regular formatting below
        } else {
          // Use relative formatting
          // Determine which time units should use "about"
          const shouldUseAboutForDays = (() => {
            if (relative === "always" || approximate === true) return true;
            return false; // "weeks", "months", "years", or false all mean no "about" for days
          })();

          const shouldUseAboutForWeeks = (() => {
            if (relative === "always" || approximate === true) return true;
            if (approximate === "weeks") return true;
            return false; // "months", "years", or false all mean no "about" for weeks
          })();

          const shouldUseAboutForMonths = (() => {
            if (relative === "always" || approximate === true) return true;
            if (approximate === "weeks" || approximate === "months") return true;
            return false; // "years" or false means no "about" for months
          })();

          const shouldUseAboutForYears = (() => {
            if (relative === "always" || approximate === true) return true;
            if (approximate === "weeks" || approximate === "months" || approximate === "years") return true;
            return false;
          })();

          if (absDiff < 7) {
            // 0-6 days: use days format
            if (shouldUseAboutForDays) {
              return isFuture
                ? i18n.t("about {{count}} days from now", { ns: "dates", count: absDiff })
                : i18n.t("about {{count}} days ago", { ns: "dates", count: absDiff });
            } else {
              return isFuture
                ? i18n.t("{{count}} days from now", { ns: "dates", count: absDiff })
                : i18n.t("{{count}} days ago", { ns: "dates", count: absDiff });
            }
          } else if (absDiff < 30) {
            // 7-29 days: use weeks format
            const weeks = Math.round(absDiff / 7);
            if (shouldUseAboutForWeeks) {
              return isFuture
                ? i18n.t("about {{count}} weeks from now", { ns: "dates", count: weeks })
                : i18n.t("about {{count}} weeks ago", { ns: "dates", count: weeks });
            } else {
              return isFuture
                ? i18n.t("{{count}} weeks from now", { ns: "dates", count: weeks })
                : i18n.t("{{count}} weeks ago", { ns: "dates", count: weeks });
            }
          } else if (absDiff < 365) {
            // 30-364 days: use months format
            const months = Math.round(absDiff / 30);
            if (shouldUseAboutForMonths) {
              return isFuture
                ? i18n.t("about {{count}} months from now", { ns: "dates", count: months })
                : i18n.t("about {{count}} months ago", { ns: "dates", count: months });
            } else {
              return isFuture
                ? i18n.t("{{count}} months from now", { ns: "dates", count: months })
                : i18n.t("{{count}} months ago", { ns: "dates", count: months });
            }
          } else {
            // 365+ days: use years format
            const years = Math.round(absDiff / 365);
            if (shouldUseAboutForYears) {
              return isFuture
                ? i18n.t("about {{count}} years from now", { ns: "dates", count: years })
                : i18n.t("about {{count}} years ago", { ns: "dates", count: years });
            } else {
              return isFuture
                ? i18n.t("{{count}} years from now", { ns: "dates", count: years })
                : i18n.t("{{count}} years ago", { ns: "dates", count: years });
            }
          }
        }
      }
    }

    // Handle numeric formats differently
    if (
      format === "MM/DD/YYYY" ||
      format === "MM/DD/YY" ||
      format === "MM-DD-YYYY" ||
      format === "MM-DD-YY" ||
      format === "DD-MM-YYYY" ||
      format === "DD-MM-YY"
    ) {
      const parts = getDatePartsInTimezone(dateObj, timezone);
      const month = String(parts.month).padStart(2, "0");
      const day = String(parts.day).padStart(2, "0");
      const year =
        format === "MM/DD/YYYY" || format === "MM-DD-YYYY" || format === "DD-MM-YYYY"
          ? parts.year
          : String(parts.year).slice(-2);

      const separator = format.includes("-") ? "-" : "/";
      const isDayFirst = format.startsWith("DD");

      return isDayFirst
        ? `${day}${separator}${month}${separator}${year}`
        : `${month}${separator}${day}${separator}${year}`;
    }

    const options: Intl.DateTimeFormatOptions =
      format === "MMM D"
        ? { month: "short", day: "numeric", timeZone: timezone }
        : format === "MMM D, YYYY"
        ? { year: "numeric", month: "short", day: "numeric", timeZone: timezone }
        : { year: "numeric", month: "long", day: "numeric", timeZone: timezone };

    return new Intl.DateTimeFormat(undefined, options).format(dateObj);
  } catch (err) {
    console.error(err);
    return "";
  }
}

/**
 * Formats the time portion of a date using the specified time format.
 *
 * @param date - The date to extract and format the time from (Date object, string, or timestamp number)
 * @param format - The time format to use. Defaults to "hh:mm a" (12-hour format with AM/PM)
 *   - "hh:mm a": 12-hour format without seconds (e.g., "2:30 PM")
 *   - "hh:mm:ss a": 12-hour format with seconds (e.g., "2:30:45 PM")
 *   - "HH:mm": 24-hour format without seconds (e.g., "14:30")
 *   - "HH:mm:ss": 24-hour format with seconds (e.g., "14:30:45")
 * @param timezone - Optional IANA timezone string (e.g., "America/New_York"). Defaults to the user's local timezone.
 * @returns The formatted time string
 *
 * @example
 * formatTime(new Date(), "hh:mm a") // "2:30 PM"
 * formatTime(new Date(), "HH:mm") // "14:30"
 * formatTime(new Date(), "hh:mm:ss a") // "2:30:45 PM"
 * formatTime(new Date(), "hh:mm a", "Europe/London") // "10:30 PM" (in London time)
 */
export function formatTime(date: Date | string | number, format: TimeFormat = "hh:mm a", timezone?: string): string {
  try {
    const dateObj = new Date(date);

    const options: Intl.DateTimeFormatOptions = (() => {
      switch (format) {
        case "hh:mm a":
          return { hour: "numeric", minute: "2-digit", hour12: true };
        case "hh:mm:ss a":
          return {
            hour: "numeric",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          };
        case "HH:mm":
          return { hour: "2-digit", minute: "2-digit", hour12: false };
        case "HH:mm:ss":
          return {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          };
        default:
          return { hour: "numeric", minute: "2-digit", hour12: true };
      }
    })();

    if (timezone) options.timeZone = timezone;

    const formatted = new Intl.DateTimeFormat(undefined, options).format(dateObj);

    // Normalize midnight (24:00) to human-readable format (00:00) for 24-hour formats
    if (format.startsWith("HH:") && formatted.startsWith("24:")) {
      return formatted.replace("24:", "00:");
    }

    return formatted;
  } catch (err) {
    console.error(err);
    return "";
  }
}

/**
 * Formats a date and time together using a combined format string, with support for date-only, time-only,
 * or full datetime formats. Includes optional relative date formatting.
 *
 * @param date - The date to format (Date object, string, or timestamp number)
 * @param format - The format string to use. Can be:
 *   - DateTimeFormat: Combined format like "MMM D, YYYY at hh:mm a"
 *   - DateFormat: Date-only format like "MMM D, YYYY"
 *   - TimeFormat: Time-only format like "hh:mm a"
 *   Defaults to "MMM D, YYYY at hh:mm a"
 * @param relative - Controls relative date formatting:
 *   - true: Returns "Today at 2:30 PM", "Tomorrow at 2:30 PM", etc. when applicable
 *   - "always": Always uses relative date formats (e.g., "2 days ago at 2:30 PM")
 *   - RelativeOptions: Fine-grained control over when to stop using relative dates
 * @param timezone - Optional IANA timezone string (e.g., "America/New_York"). Defaults to the user's local timezone.
 * @returns The formatted datetime string
 *
 * @example
 * formatDateTime(new Date(), "MMM D, YYYY at hh:mm a") // "Jan 15, 2024 at 2:30 PM"
 * formatDateTime(new Date(), "MM/DD/YYYY at hh:mm a") // "01/15/2024 at 2:30 PM"
 * formatDateTime(new Date(), "MM/DD/YY at HH:mm") // "01/15/24 at 14:30"
 * formatDateTime(new Date(), "MMM D, YYYY at hh:mm a", true) // "Today at 2:30 PM"
 * formatDateTime(new Date(), "MMM D, YYYY") // "Jan 15, 2024" (date only)
 * formatDateTime(new Date(), "hh:mm a") // "2:30 PM" (time only)
 * formatDateTime(dateInPast, "MMM D, YYYY at hh:mm a", { unit: "days", amount: 7, direction: "both" }) // "3 days ago at 2:30 PM" or "Jan 10, 2024 at 2:30 PM"
 * formatDateTime(dateInPast, "MMM D, YYYY at hh:mm a", { unit: "weeks", amount: 2, direction: "both", approximate: "months" }) // "1 week ago at 2:30 PM" (no "about") or "about 1 month ago at 2:30 PM" ("about" for months+)
 * formatDateTime(new Date(), "MMM D, YYYY at hh:mm a", false, "Asia/Tokyo") // "Jan 16, 2024 at 4:30 AM" (in Tokyo time)
 */
export function formatDateTime<T extends DateTimeFormat>(
  date: Date | string | number,
  format: T | DateFormat | TimeFormat = "MMM D, YYYY at hh:mm a" as T,
  relative: boolean | "always" | RelativeOptions = false,
  timezone?: string
): string {
  try {
    const dateObj = new Date(date);

    // Check if format is a time-only format
    if ((format as string).includes(":") && !(format as string).includes(" at ")) {
      return formatTime(dateObj, format as TimeFormat, timezone);
    }

    // Check if format is a date-only format
    if (!(format as string).includes(" at ")) {
      return formatDate(dateObj, format as DateFormat, relative, timezone);
    }

    // Extract date and time format parts using type-safe extraction
    const [dateFormat, timeFormat] = format.split(" at ") as [ExtractDateFormat<T>, ExtractTimeFormat<T>];

    // Get formatted date part (with potential relative formatting)
    const datePart = formatDate(dateObj, dateFormat, relative, timezone);

    // If relative formatting resulted in Today/Tomorrow/Yesterday, use that
    if (
      relative &&
      (datePart === i18n.t("Today", { ns: "dates" }) ||
        datePart === i18n.t("Tomorrow", { ns: "dates" }) ||
        datePart === i18n.t("Yesterday", { ns: "dates" }))
    ) {
      const timePart = formatTime(dateObj, timeFormat, timezone);
      return i18n.t(`{{datePart}} at {{timePart}}`, { ns: "dates", datePart, timePart });
    }

    if (relative === "always") {
      // For "always", dates beyond Today/Tomorrow/Yesterday return only the relative date (no time)
      if (datePart.includes("ago") || datePart.includes("from now")) {
        return datePart;
      }
      // Fall through for absolute dates or Today/Tomorrow/Yesterday (which already have time appended above)
    } else if (typeof relative === "object") {
      // For RelativeOptions, if datePart contains relative text (not a formatted date),
      // we should append the time part
      const isRelativeText =
        datePart.includes("ago") ||
        datePart.includes("from now") ||
        datePart === i18n.t("Today", { ns: "dates" }) ||
        datePart === i18n.t("Tomorrow", { ns: "dates" }) ||
        datePart === i18n.t("Yesterday", { ns: "dates" });

      if (isRelativeText) {
        const timePart = formatTime(dateObj, timeFormat, timezone);
        return i18n.t(`{{datePart}} at {{timePart}}`, { ns: "dates", datePart, timePart });
      }
      // If it's an absolute date (RelativeOptions exceeded threshold), fall through
    }

    // Otherwise, use the full formatted date with time
    const timePart = formatTime(dateObj, timeFormat, timezone);
    return i18n.t(`{{datePart}} at {{timePart}}`, { ns: "dates", datePart, timePart });
  } catch (err) {
    console.error(err);
    return "";
  }
}

/**
 * Validates whether a given input can be parsed as a valid date.
 *
 * @param date - The value to validate (Date object, string, or timestamp number)
 * @returns True if the input represents a valid date, false otherwise
 *
 * @example
 * isValidDate(new Date()) // true
 * isValidDate("2024-01-15") // true
 * isValidDate(1705315200000) // true (valid timestamp)
 * isValidDate("invalid") // false
 * isValidDate(null) // false
 * isValidDate(undefined) // false
 */
export function isValidDate(date?: Date | string | number | "") {
  if (!date) {
    return false;
  }

  try {
    const dateObj = typeof date === "string" || typeof date === "number" ? new Date(date) : date;
    return !isNaN(dateObj.getTime());
  } catch {
    return false;
  }
}
